import type {
    SketchEntityReferenceSelectionEntrySnapshot,
    SketchEntityReferenceSelectionIntentCollectionOptions,
    SketchEntityReferenceSelectionMutationInput,
    SketchEntityReferenceSelectionMutationResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSelectionIntentCollection';
import {
    resolveExternalGeometrySourceDescriptorKey,
    resolveTopologyFaceReferenceSourceKey,
} from '../../model/ExternalGeometrySourceDescriptor';
import {
    resolveSketchEntityReferenceSelectionCandidateSources,
    type SketchEntityReferenceSelectionCandidate,
} from './SketchEntityReferenceSelectionAdapter';
import type {
    SketchEntityReferenceSelectionIntent,
    SketchEntityReferenceSelectionIntentSource,
} from './SketchEntityReferenceSelectionIntent';

/**
 * 转换实体引用有序选择意图集合。
 *
 * 设计说明：
 * 1. 集合保存 PropertyManager 中用户实际选择的意图，不以去重后的执行来源替代；
 * 2. 每次 mutation 先在 next Map 上完整计算，再原子替换 current，避免部分删除；
 * 3. toggle 以实际点击来源查找首个覆盖意图；命中时只删除该意图并结束本次点击；
 * 4. 未命中覆盖意图时才追加候选，链与既有成员边因此可以按点击时序并存。
 */
export class SketchEntityReferenceSelectionIntentCollection {
    /** 当前目标草图 ID，用于拒绝自引用。 */
    private readonly targetSketchId: string;
    /** 按插入顺序保存的选择意图。 */
    private selections = new Map<string, SketchEntityReferenceSelectionEntrySnapshot>();

    /**
     * 创建选择意图集合。
     *
     * @param options 目标草图上下文。
     */
    public constructor(
        options: SketchEntityReferenceSelectionIntentCollectionOptions,
    ) {
        this.targetSketchId = options.targetSketchId;
    }

    /**
     * 应用一次选择集合操作。
     *
     * @param input 规范化操作及其候选或稳定意图 ID。
     * @returns 原子 mutation 差量和最终快照。
     */
    public apply(
        input: SketchEntityReferenceSelectionMutationInput,
    ): SketchEntityReferenceSelectionMutationResult {
        const beforeSelections = this.selections;

        // remove 不需要候选解析，按稳定意图 ID 幂等删除即可。
        if (input.operation === 'remove') {
            if (!beforeSelections.has(input.selectionId)) {
                return this.createUnchangedResult();
            }
            const nextSelections = new Map(beforeSelections);
            nextSelections.delete(input.selectionId);
            return this.commitMutation(beforeSelections, nextSelections);
        }

        // 候选必须在修改 next Map 前完成归一化和自引用校验。
        const nextEntry = this.createSelectionEntry(input.candidate);
        if (!nextEntry) {
            return this.createUnchangedResult();
        }

        const intentId = nextEntry.intent.intentId;
        if (input.operation === 'add' && beforeSelections.has(intentId)) {
            return this.createUnchangedResult();
        }

        if (input.operation === 'toggle') {
            if (
                nextEntry.intent.intentKind === 'topology-face'
                && beforeSelections.has(intentId)
            ) {
                const nextSelections = new Map(beforeSelections);
                nextSelections.delete(intentId);
                const faceSource = nextEntry.intent.topologyFaceSource;
                if (faceSource) {
                    const faceSourceKey = resolveTopologyFaceReferenceSourceKey(faceSource);
                    // 面是内环选择的组根；再次点击面必须一次性撤销整个面组。
                    nextSelections.forEach((selection, selectionId) => {
                        const selectionFaceSource = selection.intent.topologyFaceSource;
                        if (
                            selection.intent.intentKind === 'topology-loop'
                            && selectionFaceSource
                            && resolveTopologyFaceReferenceSourceKey(selectionFaceSource)
                                === faceSourceKey
                        ) {
                            nextSelections.delete(selectionId);
                        }
                    });
                }
                return this.commitMutation(beforeSelections, nextSelections);
            }

            if (nextEntry.intent.intentKind !== 'topology-face') {
                const coveredSelectionId = this.resolveFirstSelectionCoveringInteractionSource(
                    beforeSelections,
                    input.candidate,
                );
                if (coveredSelectionId) {
                    const nextSelections = new Map(beforeSelections);
                    nextSelections.delete(coveredSelectionId);
                    return this.commitMutation(beforeSelections, nextSelections);
                }
            }
        }

        if (input.operation === 'replace') {
            const existingEntry = beforeSelections.get(intentId);
            if (
                beforeSelections.size === 1
                && existingEntry
                && this.areEntriesEqual(existingEntry, nextEntry)
            ) {
                return this.createUnchangedResult();
            }
            return this.commitMutation(
                beforeSelections,
                new Map([[intentId, nextEntry]]),
            );
        }

        const nextSelections = new Map(beforeSelections);
        this.removeIncompatibleSelections(nextSelections, nextEntry);
        nextSelections.set(intentId, nextEntry);
        return this.commitMutation(beforeSelections, nextSelections);
    }

    /**
     * 原子追加一组选择候选。
     *
     * 所有候选先在独立 Map 上复用单条兼容规则；只有整批处理完成后才一次替换正式集合。
     * 任一候选处理抛错时恢复原集合，避免“选择所有内环”留下部分结果。
     *
     * @param candidates 按 Face/Wire 顺序排列的候选。
     * @returns 整批操作相对原集合的唯一 mutation。
     */
    public applyBatch(
        candidates: readonly SketchEntityReferenceSelectionCandidate[],
    ): SketchEntityReferenceSelectionMutationResult {
        if (candidates.length === 0) {
            return this.createUnchangedResult();
        }
        const beforeSelections = this.selections;
        this.selections = new Map(beforeSelections);
        try {
            candidates.forEach((candidate) => {
                this.apply({
                    operation: 'add',
                    candidate,
                });
            });
            const nextSelections = this.selections;
            this.selections = beforeSelections;
            return this.commitMutation(beforeSelections, nextSelections);
        } catch (error) {
            this.selections = beforeSelections;
            throw error;
        }
    }

    /**
     * 清空全部选择意图。
     *
     * @returns 清空操作的差量和最终空快照。
     */
    public clear(): SketchEntityReferenceSelectionMutationResult {
        if (this.selections.size === 0) {
            return this.createUnchangedResult();
        }
        return this.commitMutation(this.selections, new Map());
    }

    /**
     * 读取当前有序选择快照。
     *
     * @returns 与内部 Map 解耦的只读快照。
     */
    public getSelections(): readonly SketchEntityReferenceSelectionEntrySnapshot[] {
        return Array.from(this.selections.values()).map((entry) => (
            this.cloneSelectionEntry(entry)
        ));
    }

    /**
     * 把候选归一化为完整选择意图。
     *
     * @param candidate cad-core 候选解析器输出。
     * @returns 合法选择 entry；自引用或无来源时返回 `null`。
     */
    private createSelectionEntry(
        candidate: SketchEntityReferenceSelectionCandidate,
    ): SketchEntityReferenceSelectionEntrySnapshot | null {
        const candidateSources = resolveSketchEntityReferenceSelectionCandidateSources(candidate);
        if (candidateSources.length === 0) {
            return null;
        }

        // 任何展开来源指向目标草图都会形成自引用，因此整条复合意图必须拒绝。
        if (candidateSources.some((source) => (
            source.descriptor.kind === 'sketch-entity'
            && source.descriptor.sourceSketchId === this.targetSketchId
        ))) {
            return null;
        }

        const intent = candidate.intent
            ? { ...candidate.intent }
            : this.createSingleSourceIntent(candidate);
        const sources = candidateSources.map((source) => ({
            descriptor: source.descriptor,
            displayLabel: source.displayLabel,
            sourceObject: source.sourceObject,
            runtimeTopologySource: source.runtimeTopologySource,
        }));
        return {
            intent,
            sources,
            visualSourceObject: candidate.visualSourceObject,
            innerLoopCount: candidate.innerLoopCount,
        };
    }

    /**
     * 为普通候选创建稳定的单来源意图。
     *
     * @param candidate 普通单来源候选。
     * @returns 单来源选择意图。
     */
    private createSingleSourceIntent(
        candidate: SketchEntityReferenceSelectionCandidate,
    ): SketchEntityReferenceSelectionIntent {
        return {
            intentKind: 'single-source',
            intentId: resolveExternalGeometrySourceDescriptorKey(candidate.descriptor),
            displayKind: 'edge',
            displayLabel: candidate.displayLabel,
        };
    }

    /**
     * 查找按 PropertyManager 顺序首个覆盖实际点击来源的选择意图。
     *
     * 说明：
     * 1. 链候选的 `descriptor` 是规范展开首项，不一定是用户实际点击的成员边；
     * 2. resolver 会让 `candidate.sourceObject` 指向实际命中对象，因此优先用对象身份
     *    在 `relatedSources` 中还原点击来源；
     * 3. 找到覆盖意图后由调用方只删除这一项，并终止本次点击，不再追加新候选。
     *
     * @param selections 当前有序选择集合。
     * @param candidate 本次点击解析出的候选。
     * @returns 首个覆盖意图 ID；没有覆盖时返回 `null`。
     */
    private resolveFirstSelectionCoveringInteractionSource(
        selections: ReadonlyMap<string, SketchEntityReferenceSelectionEntrySnapshot>,
        candidate: SketchEntityReferenceSelectionCandidate,
    ): string | null {
        const interactionDescriptor = candidate.relatedSources?.find((source) => (
            candidate.sourceObject !== undefined
            && source.sourceObject === candidate.sourceObject
        ))?.descriptor ?? candidate.descriptor;
        const interactionSourceKey =
            resolveExternalGeometrySourceDescriptorKey(interactionDescriptor);

        for (const [selectionId, selection] of selections) {
            const coversInteractionSource = selection.sources.some((source) => (
                resolveExternalGeometrySourceDescriptorKey(source.descriptor)
                === interactionSourceKey
            ));
            if (coversInteractionSource) {
                return selectionId;
            }
        }

        return null;
    }

    /**
     * 删除与新增意图不兼容的旧选择。
     *
     * @param nextSelections 正在计算的下一状态。
     * @param nextEntry 待加入的选择意图。
     */
    private removeIncompatibleSelections(
        nextSelections: Map<string, SketchEntityReferenceSelectionEntrySnapshot>,
        nextEntry: SketchEntityReferenceSelectionEntrySnapshot,
    ): void {
        const nextSourceKeys = new Set(
            nextEntry.sources.map((source) => (
                resolveExternalGeometrySourceDescriptorKey(source.descriptor)
            )),
        );

        Array.from(nextSelections.entries()).forEach(([selectionId, existingEntry]) => {
            const sharesSource = existingEntry.sources.some((source) => (
                nextSourceKeys.has(
                    resolveExternalGeometrySourceDescriptorKey(source.descriptor),
                )
            ));
            if (!sharesSource || this.canSelectionsShareSources(nextEntry, existingEntry)) {
                return;
            }
            nextSelections.delete(selectionId);
        });
    }

    /**
     * 判断两条选择意图能否共享展开来源。
     *
     * @param nextEntry 新增意图。
     * @param existingEntry 已有意图。
     * @returns 两条意图允许并存时返回 `true`。
     */
    private canSelectionsShareSources(
        nextEntry: SketchEntityReferenceSelectionEntrySnapshot,
        existingEntry: SketchEntityReferenceSelectionEntrySnapshot,
    ): boolean {
        const nextKind = nextEntry.intent.intentKind;
        const existingKind = existingEntry.intent.intentKind;

        // 相邻面允许共享边；有效来源编译器会按稳定拓扑身份去重并生成一次。
        if (nextKind === 'topology-face' && existingKind === 'topology-face') {
            return true;
        }

        // face 的 outer Wire 与 loop 的 inner Wire 本应不共享来源；若异常共享则拒绝并存。
        if (nextKind === 'topology-loop' || existingKind === 'topology-loop') {
            return false;
        }

        // 草图链之间以及链与成员单边都保留独立 PropertyManager 意图。
        if (nextKind === 'sketch-chain') {
            return existingKind === 'sketch-chain' || existingKind === 'single-source';
        }
        if (existingKind === 'sketch-chain') {
            return nextKind === 'single-source';
        }

        return false;
    }

    /**
     * 提交完整 next Map，并生成 ID 与内容级差量。
     *
     * @param beforeSelections mutation 前状态。
     * @param nextSelections 完整计算后的下一状态。
     * @returns mutation 结果。
     */
    private commitMutation(
        beforeSelections: Map<string, SketchEntityReferenceSelectionEntrySnapshot>,
        nextSelections: Map<string, SketchEntityReferenceSelectionEntrySnapshot>,
    ): SketchEntityReferenceSelectionMutationResult {
        const removedSelections = Array.from(beforeSelections.entries())
            .filter(([selectionId, beforeEntry]) => {
                const nextEntry = nextSelections.get(selectionId);
                return !nextEntry || !this.areEntriesEqual(beforeEntry, nextEntry);
            })
            .map(([, entry]) => this.cloneSelectionEntry(entry));
        const addedSelections = Array.from(nextSelections.entries())
            .filter(([selectionId, nextEntry]) => {
                const beforeEntry = beforeSelections.get(selectionId);
                return !beforeEntry || !this.areEntriesEqual(beforeEntry, nextEntry);
            })
            .map(([, entry]) => this.cloneSelectionEntry(entry));
        const changed = removedSelections.length > 0 || addedSelections.length > 0;

        if (changed) {
            this.selections = nextSelections;
        }

        return {
            changed,
            addedSelections,
            removedSelections,
            selections: this.getSelections(),
        };
    }

    /**
     * 创建不改变集合的结果。
     *
     * @returns 空差量和当前完整快照。
     */
    private createUnchangedResult(): SketchEntityReferenceSelectionMutationResult {
        return {
            changed: false,
            addedSelections: [],
            removedSelections: [],
            selections: this.getSelections(),
        };
    }

    /**
     * 比较两条选择 entry 的稳定业务内容。
     *
     * @param left 左侧 entry。
     * @param right 右侧 entry。
     * @returns 意图字段和有序来源键均一致时返回 `true`。
     */
    private areEntriesEqual(
        left: SketchEntityReferenceSelectionEntrySnapshot,
        right: SketchEntityReferenceSelectionEntrySnapshot,
    ): boolean {
        if (
            left.intent.intentKind !== right.intent.intentKind
            || left.intent.intentId !== right.intent.intentId
            || left.intent.displayKind !== right.intent.displayKind
            || left.intent.displayLabel !== right.intent.displayLabel
            || left.visualSourceObject !== right.visualSourceObject
            || left.innerLoopCount !== right.innerLoopCount
            || left.sources.length !== right.sources.length
        ) {
            return false;
        }

        return left.sources.every((source, index) => {
            const otherSource = right.sources[index];
            return otherSource !== undefined
                && source.displayLabel === otherSource.displayLabel
                && source.sourceObject === otherSource.sourceObject
                && resolveExternalGeometrySourceDescriptorKey(source.descriptor)
                    === resolveExternalGeometrySourceDescriptorKey(otherSource.descriptor);
        });
    }

    /**
     * 克隆一条会话选择快照。
     *
     * @param entry 内部 entry。
     * @returns 与内部数组和意图对象解耦的快照。
     */
    private cloneSelectionEntry(
        entry: SketchEntityReferenceSelectionEntrySnapshot,
    ): SketchEntityReferenceSelectionEntrySnapshot {
        return {
            intent: {
                ...entry.intent,
            },
            visualSourceObject: entry.visualSourceObject,
            innerLoopCount: entry.innerLoopCount,
            sources: entry.sources.map((
                source: SketchEntityReferenceSelectionIntentSource,
            ) => ({
                descriptor: source.descriptor,
                displayLabel: source.displayLabel,
                sourceObject: source.sourceObject,
                runtimeTopologySource: source.runtimeTopologySource,
            })),
        };
    }
}
